import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import PrivateRoute from './PrivateRoute'
import DesktopOnlyOutlet from './DesktopOnlyOutlet'
import PrivateOutlet from './PrivateOutlet'
import FieldOutlet from 'features/Field/FieldOutlet'
import AppEntry from 'features/Field/AppEntry'
import FieldHome from 'features/Field/screens/FieldHome'
import FieldProjects from 'features/Field/screens/FieldProjects'
import FieldPartners from 'features/Field/screens/FieldPartners'
import FieldImages from 'features/Field/screens/FieldImages'
import FieldTasks from 'features/Field/screens/FieldTasks'
import NearMe from 'features/Field/screens/NearMe'
import NewSite from 'features/Field/screens/NewSite'
import FieldRecord from 'features/Field/screens/FieldRecord'
import { fieldRoutes } from 'constants/fieldRoutes'
import { DeckEditorScreen, PresentationsScreen, PresenterScreen, PresenterView, PublicPresentationScreen, isPresentationsEnabled } from 'features/Presentations'
import screens from 'screens'
import routes from 'constants/routes'

// Evaluation nested-route children (rendered inside Evaluations_ShowCN's <Outlet />).
// These are co-located with the parent screen and not exported via the screens barrel.
import EvaluationOverview from 'screens/evaluations/components/EvaluationOverview'
import EvaluationSites from 'screens/evaluations/components/EvaluationSites'
import EvaluationResources from 'screens/evaluations/components/EvaluationResources'
import VendorResource from 'screens/evaluations/components/resources/VendorResource'
import { VENDORS, sortVendors } from 'features/Vendors/vendors'
import EvaluationScoring from 'screens/evaluations/components/EvaluationScoring'
import EvaluationFinancials from 'screens/evaluations/components/EvaluationFinancials'
import EvaluationCompare from 'screens/evaluations/components/EvaluationCompare'
import EvaluationRisks from 'screens/evaluations/components/EvaluationRisks'
import EvaluationProsCons from 'screens/evaluations/components/EvaluationProsCons'
import EvaluationApprovals from 'screens/evaluations/components/EvaluationApprovals'
import EvaluationActivity from 'screens/evaluations/components/EvaluationActivity'
import EvaluationReports from 'screens/evaluations/components/EvaluationReports'

// Global evaluation-module pages (SSP-parity dual routing). Each shows an
// evaluation picker and embeds the matching workspace once one is picked.
import ScoringPage from 'screens/evaluations/pages/ScoringPage'
import ComparePage from 'screens/evaluations/pages/ComparePage'
import FinancialsPage from 'screens/evaluations/pages/FinancialsPage'
import RisksPage from 'screens/evaluations/pages/RisksPage'
import ProsConsPage from 'screens/evaluations/pages/ProsConsPage'
import ApprovalsPage from 'screens/evaluations/pages/ApprovalsPage'
import ReportsPage from 'screens/evaluations/pages/ReportsPage'
import EvaluationMap from 'screens/evaluations/EvaluationMap'
import MyEvaluationSites from 'screens/evaluations/MyEvaluationSites'
import Sites_ShowCN from 'screens/evaluations/Sites_ShowCN'
import SiteOverview from 'screens/evaluations/navPages/SiteOverview'
import SiteImages from 'screens/evaluations/navPages/SiteImages'

import SiteNotes from 'screens/evaluations/navPages/SiteNotes'

// Public document-share viewer (no auth — token is the auth).
import { DocumentSharePage } from 'features/DocumentLibrary'

// Passwordless trial/marketing flows.
import MagicLoginScreen from 'screens/trial/MagicLoginScreen'
import UnsubscribeScreen from 'features/Notifications/screens/UnsubscribeScreen'
import ConvertAccountScreen from 'screens/trial/ConvertAccountScreen'
import CreateTrialUserCN from 'screens/admin/contentPages/CreateTrialUserCN'
import SupportImpersonateCN from 'screens/admin/contentPages/SupportImpersonateCN'
import HelpDocsCN from 'screens/admin/contentPages/HelpDocsCN'
import VendorsScreen from 'screens/admin/contentPages/VendorsScreen'

// A plan proposed from the Claude.ai connector, confirmed here (MYD-648).
import PlanScreen from 'features/AskMdt/screens/PlanScreen'

// Extract components for use in JSX
const {
	ExplorerScreen,
	LoginScreen,
	Home,
	Community_Page,
	Locations_New,
	Locations_IndexCN,
	Locations_ShowCN,
	Locations_Projects_ShowCN,
	Portfolios_IndexCN,
	Portfolios_ShowCN,
	Evaluations_IndexCN,
	Evaluations_ShowCN,
	PartnerApps_Index,
	PartnerApps_Show,
	Projects_IndexCN,
	Projects_ShowCN,
	TasksCN_Index,
	Templates_Index,
	TemplateTask_Show,
	TemplateSiteDetail_Show,
	Partners_Index,
	Admin_Index,
	Admin_Show,
	ImagesGalleryCN,
	MyLibraryDocumentsCN,
	PortfoliosListCN,
	ProjectsListCN,
	LocationsListCN,
	BulkLoadLocationsCN,
	TasksTemplateCN,
	SiteDetailsTemplateCN,
	ScoringCriteriaTemplateCN,
	AcquisitionTemplateCN,
	OperatingTemplateCN,
	ROIAnalysisTemplateCN,
	RiskTemplateCN,
	ProsConsTemplateCN,
	LookupListsCN,
	CleanupCN,
	AiUsageCN,
	UserProfileCN,
	OAuthConsent,
	FourOhFour,
} = screens

const AppRoutes = () => (
	<Routes>
		{/* Login/Register */}
		<Route path={routes.LOGIN} element={<LoginScreen />} />
		<Route path={routes.REGISTER} element={<LoginScreen />} />
		<Route path={routes.FORGOT_PASSWORD} element={<LoginScreen />} />
		<Route path={routes.CHANGE_PASSWORD} element={<LoginScreen />} />
		<Route path={routes.CONFIRM_PASS_EMAIL} element={<LoginScreen />} />
		<Route path={routes.EMAIL_CONFIRM} element={<LoginScreen />} />
		<Route path={routes.REGISTER_CONFIRM} element={<LoginScreen />} />

		{/* Public document share viewer — token in URL is the auth */}
		<Route path="/share/document/:token" element={<DocumentSharePage />} />

		{/* Passwordless magic-link login — token in URL is the auth */}
		<Route path={routes.MAGIC_LOGIN} element={<MagicLoginScreen />} />

		{/* An email's "Unsubscribe" link (MYD-546) — token in URL is the auth */}
		<Route path={routes.UNSUBSCRIBE} element={<UnsubscribeScreen />} />

		{/* Trial -> real account conversion / lockout (requires a session) */}
		<Route
			path={routes.CONVERT_ACCOUNT}
			element={
				<PrivateRoute>
					<ConvertAccountScreen />
				</PrivateRoute>
			}
		/>

		{/* Admin: create passwordless trial/marketing accounts */}
		<Route
			path={routes.ADMIN_TRIAL_USERS}
			element={
				<PrivateRoute>
					<CreateTrialUserCN />
				</PrivateRoute>
			}
		/>

		{/* A plan the Claude.ai connector proposed (MYD-648). Not desktop-only:
		    the link is opened from wherever Claude.ai was, a phone included. */}
		<Route
			path={routes.AI_PLAN}
			element={
				<PrivateRoute>
					<PlanScreen />
				</PrivateRoute>
			}
		/>

		{/* Admin: support impersonation (master password) */}
		<Route
			path={routes.ADMIN_SUPPORT}
			element={
				<PrivateRoute>
					<SupportImpersonateCN />
				</PrivateRoute>
			}
		/>

		{/* Admin: Support > Help Docs. Desktop-only: the walkthrough reaches a
		    phone through the field account menu instead. */}
		<Route
			path={routes.ADMIN_HELP_DOCS}
			element={
				<PrivateRoute>
					<HelpDocsCN />
				</PrivateRoute>
			}
		/>

		{/* Protected Routes */}
		<Route
			path={routes.HOME}
			element={
				<PrivateRoute>
					{/* Field app or desktop home, from the stored preference with a
					    device-class fallback. See features/Field/useAppMode. */}
					<AppEntry desktop={<Home />} />
				</PrivateRoute>
			}
		/>

		{/*
			The desktop home, addressable directly.

			`/` goes through AppEntry, which on a phone always resolves to the
			field app. That is right: the field app IS mobile MYDealTeams. But a
			phone still has to be able to ASK for the desktop home, so it gets a
			URL that no mode decision sits in front of.

			Deliberately not a stored preference. MYD-429 removed that because it
			could strand a user in the wrong app with no way back. A plain link is
			a two-way door: HeaderCN carries the way back to the field app.
		*/}
		<Route
			path={routes.DESKTOP_HOME}
			element={
				<PrivateRoute>
					<Home />
				</PrivateRoute>
			}
		/>

		{/*
			The field app.

			Its own namespace rather than swapping components underneath the
			existing URLs: these are different screens with a different navigation
			shape, not responsive variants. Guarded once by PrivateOutlet instead of
			per route, and NOT gated on viewport — a field URL is legal anywhere, and
			constants/fieldRoutes maps between the two namespaces so a link survives
			being sent from a phone to a desk.
		*/}
		<Route element={<PrivateOutlet />}>
			<Route element={<FieldOutlet />}>
				<Route path={fieldRoutes.HOME} element={<FieldHome />} />
				<Route path={fieldRoutes.NEAR} element={<NearMe />} />
				<Route path={fieldRoutes.NEW_SITE} element={<NewSite />} />
				<Route path={fieldRoutes.TASKS} element={<FieldTasks />} />
				<Route path={fieldRoutes.PROJECTS} element={<FieldProjects />} />
				<Route path={fieldRoutes.PARTNERS} element={<FieldPartners />} />
				<Route path={fieldRoutes.IMAGES} element={<FieldImages />} />
				<Route path={fieldRoutes.PROJECT} element={<FieldRecord kind="project" />} />
				<Route path={fieldRoutes.SITE} element={<FieldRecord kind="site" />} />
				<Route path={fieldRoutes.LOCATION} element={<FieldRecord kind="location" />} />
			</Route>
		</Route>

		{/* The global chat is no longer a page. It is a modal popup opened from
		    the header Chat icon and available on every screen, so /chats has no
		    destination. Kept as a redirect for one release so old bookmarks and
		    the ?id= deep links people already have do not 404. */}
		<Route path={routes.CHAT} element={<Navigate to={routes.HOME} replace />} />

		{/* Community */}
		<Route
			path={routes.COMMUNITY}
			element={
				<PrivateRoute>
					<Community_Page />
				</PrivateRoute>
			}
		/>

		{/* Locations */}
		<Route
			path={routes.LOCATIONS}
			element={
				<PrivateRoute>
					<Locations_IndexCN />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.LOCATIONS}/new`}
			element={
				<PrivateRoute>
					<Locations_New />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.LOCATIONS}/:id/projects/:projectId/*`}
			element={
				<PrivateRoute>
					<Locations_Projects_ShowCN />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.LOCATIONS}/:id/*`}
			element={
				<PrivateRoute>
					<Locations_ShowCN />
				</PrivateRoute>
			}
		/>

		{/* Portfolios */}
		<Route
			path={routes.PORTFOLIOS}
			element={
				<PrivateRoute>
					<Portfolios_IndexCN />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.PORTFOLIOS}/:id/*`}
			element={
				<PrivateRoute>
					<Portfolios_ShowCN />
				</PrivateRoute>
			}
		/>

		{/* Desktop only. The evaluation workspaces are dense multi-pane screens,
		    and the global module routes below (compare/scoring/financials/risks/
		    proscons) have no nav entry, so a bookmark is the only way in. Wrapping
		    the /evaluations/:id parent covers its <Outlet/> children too. */}
		<Route element={<DesktopOnlyOutlet />}>
			{/* Evaluations */}
			<Route
				path={routes.EVALUATIONS}
				element={
					<PrivateRoute>
						<Evaluations_IndexCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.EVALUATIONS}/:id/map`}
				element={
					<PrivateRoute>
						<EvaluationMap />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.EVALUATIONS}/:id`}
				element={
					<PrivateRoute>
						<Evaluations_ShowCN />
					</PrivateRoute>
				}
			>
				<Route index element={<EvaluationOverview />} />
				<Route path="sites" element={<EvaluationSites />} />
				<Route path="resources" element={<EvaluationResources />}>
					<Route index element={<Navigate to={sortVendors(VENDORS)[0].id} replace />} />
					<Route path=":vendorId" element={<VendorResource />} />
				</Route>
				<Route path="scoring" element={<EvaluationScoring />} />
				<Route path="financials" element={<EvaluationFinancials />} />
				<Route path="compare" element={<EvaluationCompare />} />
				<Route path="risks" element={<EvaluationRisks />} />
				<Route path="proscons" element={<EvaluationProsCons />} />
				<Route path="approvals" element={<EvaluationApprovals />} />
				<Route path="activity" element={<EvaluationActivity />} />
				<Route path="reports" element={<EvaluationReports />} />
			</Route>

			{/* Global evaluation-module routes (SSP parity) */}
			<Route
				path={routes.COMPARE}
				element={
					<PrivateRoute>
						<ComparePage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.SCORING}
				element={
					<PrivateRoute>
						<ScoringPage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.FINANCIALS}
				element={
					<PrivateRoute>
						<FinancialsPage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.RISKS}
				element={
					<PrivateRoute>
						<RisksPage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.PROSCONS}
				element={
					<PrivateRoute>
						<ProsConsPage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.APPROVALS}
				element={
					<PrivateRoute>
						<ApprovalsPage />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.REPORTS}
				element={
					<PrivateRoute>
						<ReportsPage />
					</PrivateRoute>
				}
			/>
		</Route>
		{/* Partner Apps */}
		<Route
			path={routes.PARTNERAPPS}
			element={
				<PrivateRoute>
					<PartnerApps_Index />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.PARTNERAPPS}/:id/*`}
			element={
				<PrivateRoute>
					<PartnerApps_Show />
				</PrivateRoute>
			}
		/>

		{/* Projects */}
		<Route
			path={routes.PROJECTS}
			element={
				<PrivateRoute>
					<Projects_IndexCN />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.PROJECTS}/:id/*`}
			element={
				<PrivateRoute>
					<Projects_ShowCN />
				</PrivateRoute>
			}
		/>

		{/* Desktop only. Both halves: gating just the grain route would let a tap
		    on /explorer rewrite the URL, then block, so Back would redirect forward
		    again and trap the user. */}
		<Route element={<DesktopOnlyOutlet />}>
			{/* Explorer. The bare path redirects to the first available grain, so a
			    link to /explorer always lands on a real report rather than a chooser. */}
			<Route path={routes.EXPLORER} element={<Navigate to={routes.EXPLORER_LOCATIONS} replace />} />
			<Route
				path={routes.EXPLORER_GRAIN}
				element={
					<PrivateRoute>
						<ExplorerScreen />
					</PrivateRoute>
				}
			/>
		</Route>

		{/* Presentations (MYD-494), behind REACT_APP_PRESENTATIONS_ENABLED until
		    the editor and presenter ship. The gallery and editor are desktop
		    work; the present route deliberately sits OUTSIDE DesktopOnlyOutlet,
		    because people present from an iPad and open shared decks on a phone. */}
		{isPresentationsEnabled() && (
			<>
				<Route element={<DesktopOnlyOutlet />}>
					<Route
						path={routes.PRESENTATIONS}
						element={
							<PrivateRoute>
								<PresentationsScreen />
							</PrivateRoute>
						}
					/>
					<Route
						path={routes.PRESENTATION_EDIT}
						element={
							<PrivateRoute>
								<DeckEditorScreen />
							</PrivateRoute>
						}
					/>
				</Route>
				<Route
					path={routes.PRESENTATION_PRESENT}
					element={
						<PrivateRoute>
							<PresenterScreen />
						</PrivateRoute>
					}
				/>
				{/* The presenter's second window (MYD-497). Not desktop-only either:
				    presenting from an iPad with notes on a laptop is a real setup. */}
				<Route
					path={routes.PRESENTATION_PRESENTER_VIEW}
					element={
						<PrivateRoute>
							<PresenterView />
						</PrivateRoute>
					}
				/>
				{/* A public share link (MYD-509): no login, the token is the auth.
				    Not desktop-only: a recipient opens it on whatever they have. */}
				<Route path={routes.PRESENTATION_SHARE} element={<PublicPresentationScreen />} />
			</>
		)}

		{/* Tasks */}
		<Route
			path={routes.TASKSCN}
			element={
				<PrivateRoute>
					<TasksCN_Index />
				</PrivateRoute>
			}
		/>

		{/* Templates */}
		<Route
			path={routes.TEMPLATES}
			element={
				<PrivateRoute>
					<Templates_Index />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.TEMPLATES}/:id/task/*`}
			element={
				<PrivateRoute>
					<TemplateTask_Show />
				</PrivateRoute>
			}
		/>
		<Route
			path={`${routes.TEMPLATES}/:id/sitedetail/*`}
			element={
				<PrivateRoute>
					<TemplateSiteDetail_Show />
				</PrivateRoute>
			}
		/>

		{/* Partners */}
		<Route
			path={routes.PARTNERS}
			element={
				<PrivateRoute>
					<Partners_Index />
				</PrivateRoute>
			}
		/>

		{/* Desktop only: the ADMIN panel sections plus Evaluation Sites. */}
		<Route element={<DesktopOnlyOutlet />}>
			{/* Admin */}
			<Route
				path={routes.ADMIN}
				element={
					<PrivateRoute>
						<Admin_Index />
					</PrivateRoute>
				}
			/>
			<Route
				path={routes.ADMIN_VENDORS}
				element={
					<PrivateRoute>
						<VendorsScreen />
					</PrivateRoute>
				}
			/>
			{/*
				Per-user "My Documents" rollup — every libraryDocument the
				current user owns, across project/portfolio/global scopes.
				MYD-303 repointed this URL from the workspace-wide admin
				library (MYD-299/302) to the personal rollup users were
				actually expecting.
			*/}
			<Route
				path={`${routes.ADMIN}/library/documents`}
				element={
					<PrivateRoute>
						<MyLibraryDocumentsCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/library/images`}
				element={
					<PrivateRoute>
						<ImagesGalleryCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/archives/locations`}
				element={
					<PrivateRoute>
						<LocationsListCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/archives/portfolios`}
				element={
					<PrivateRoute>
						<PortfoliosListCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/archives/projects`}
				element={
					<PrivateRoute>
						<ProjectsListCN />
					</PrivateRoute>
				}
			/>
			{/* Evaluation Sites — canonical path, reached from MAIN/Evaluations. */}
			<Route
				path={routes.ALL_SITES}
				element={
					<PrivateRoute>
						<MyEvaluationSites />
					</PrivateRoute>
				}
			/>
			{/* Site detail. Top-level rather than nested under an evaluation: a
			    site is owner-scoped and may belong to zero or many evaluations, so
			    one route serves both the evaluation Sites tab and the
			    operator-wide Evaluation Sites page. Declared after the index route
			    above so the static path still wins. */}
			<Route
				path={`${routes.ALL_SITES}/:siteId`}
				element={
					<PrivateRoute>
						<Sites_ShowCN />
					</PrivateRoute>
				}
			>
				<Route index element={<SiteOverview />} />
				<Route path="images" element={<SiteImages />} />
				<Route path="notes" element={<SiteNotes />} />
				
			</Route>
		</Route>
		{/* Its previous home under ADMIN/Archives. Redirects rather than
		    rendering a second copy, so there is one canonical URL and the
		    sidebar's active state resolves correctly. */}
		<Route
			path={routes.ALL_SITES_LEGACY}
			element={<Navigate to={routes.ALL_SITES} replace />}
		/>
		{/* Desktop only: ADMIN Templates and Utilities. */}
		<Route element={<DesktopOnlyOutlet />}>
			<Route
				path={`${routes.ADMIN}/utilities/bulkload`}
				element={
					<PrivateRoute>
						<BulkLoadLocationsCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/tasks`}
				element={
					<PrivateRoute>
						<TasksTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/sitedetails`}
				element={
					<PrivateRoute>
						<SiteDetailsTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/scoring-criteria`}
				element={
					<PrivateRoute>
						<ScoringCriteriaTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/acquisition`}
				element={
					<PrivateRoute>
						<AcquisitionTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/operating`}
				element={
					<PrivateRoute>
						<OperatingTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/roi-analysis`}
				element={
					<PrivateRoute>
						<ROIAnalysisTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/risks`}
				element={
					<PrivateRoute>
						<RiskTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/templates/pros-cons`}
				element={
					<PrivateRoute>
						<ProsConsTemplateCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/utilities/lookuplists`}
				element={
					<PrivateRoute>
						<LookupListsCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/utilities/cleanup`}
				element={
					<PrivateRoute>
						<CleanupCN />
					</PrivateRoute>
				}
			/>
			<Route
				path={`${routes.ADMIN}/utilities/ai-usage`}
				element={
					<PrivateRoute>
						<AiUsageCN />
					</PrivateRoute>
				}
			/>
		</Route>
		<Route
			path={`${routes.ADMIN}/userprofile`}
			element={
				<PrivateRoute>
					<UserProfileCN />
				</PrivateRoute>
			}
		/>
		{/* Desktop only. Static routes above — userprofile, trial-users, support —
		    outrank this in react-router, so they stay reachable on mobile. */}
		<Route element={<DesktopOnlyOutlet />}>
			<Route
				path={`${routes.ADMIN}/:toolType/*`}
				element={
					<PrivateRoute>
						<Admin_Show />
					</PrivateRoute>
				}
			/>
		</Route>

		{/* OAuth consent page. OAUTH_CONSENT is the canonical browser route (the
		    OAuth authorization_endpoint); OAUTH_AUTHORIZE is kept for backward
		    compatibility in dev where CRA's proxy serves the SPA for navigations. */}
		<Route path={routes.OAUTH_CONSENT} element={<OAuthConsent />} />
		<Route path={routes.OAUTH_AUTHORIZE} element={<OAuthConsent />} />

		{/* 404 Not Found */}
		<Route path="*" element={<FourOhFour />} />
	</Routes>
)

export default AppRoutes
